import {
  useMemo,
  useRef,
  useState,
  type DragEvent,
  type KeyboardEvent,
  type ReactElement,
} from 'react';
import { useTranslation } from 'react-i18next';
import { GripVertical } from 'lucide-react';

import { cn } from '@/lib/utils';
import { useSidePanelOrder } from '@/features/patient-record/hooks/useSidePanelOrder';

import type { SortablePanelItem } from './types';

interface SortableSidePanelsProps {
  readonly items: readonly SortablePanelItem[];
}

/**
 * Vertical sortable container for the patient side rail. Uses the
 * native HTML5 drag-and-drop API (no extra dependency). The grip handle
 * inside each panel header is the drag source — limiting drag origin to
 * the handle keeps clicks on the toggle/edit buttons from accidentally
 * starting a reorder. Each card wrapper is the drop target. Order
 * persists via `useSidePanelOrder`; arrow keys on the grip nudge.
 */
export function SortableSidePanels({
  items,
}: SortableSidePanelsProps): ReactElement {
  const { t } = useTranslation();
  const ids = useMemo(() => items.map((item) => item.id), [items]);
  const { order, move, nudge } = useSidePanelOrder(ids);

  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [dropTargetId, setDropTargetId] = useState<string | null>(null);
  const cardRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  const ordered = useMemo(() => {
    const byId = new Map(items.map((item) => [item.id, item]));
    return order
      .map((id) => byId.get(id))
      .filter((item): item is SortablePanelItem => item !== undefined);
  }, [items, order]);

  const handleHandleDragStart =
    (id: string) =>
    (event: DragEvent<HTMLButtonElement>): void => {
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/plain', id);
      const card = cardRefs.current.get(id);
      if (card !== undefined) {
        // Use the whole card as the drag preview rather than the grip alone.
        event.dataTransfer.setDragImage(card, 12, 12);
      }
      setDraggingId(id);
    };

  const handleHandleDragEnd = (): void => {
    setDraggingId(null);
    setDropTargetId(null);
  };

  const handleHandleKeyDown =
    (id: string) =>
    (event: KeyboardEvent<HTMLButtonElement>): void => {
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        nudge(id, -1);
      } else if (event.key === 'ArrowDown') {
        event.preventDefault();
        nudge(id, 1);
      }
    };

  const handleCardDragOver =
    (id: string) =>
    (event: DragEvent<HTMLDivElement>): void => {
      if (draggingId === null || draggingId === id) return;
      event.preventDefault();
      event.dataTransfer.dropEffect = 'move';
      if (dropTargetId !== id) setDropTargetId(id);
    };

  const handleCardDragLeave =
    (id: string) =>
    (event: DragEvent<HTMLDivElement>): void => {
      if (event.currentTarget.contains(event.relatedTarget as Node | null)) {
        return;
      }
      setDropTargetId((current) => (current === id ? null : current));
    };

  const handleCardDrop =
    (id: string) =>
    (event: DragEvent<HTMLDivElement>): void => {
      event.preventDefault();
      const sourceId = event.dataTransfer.getData('text/plain') || draggingId;
      if (sourceId !== null && sourceId !== '' && sourceId !== id) {
        move(sourceId, id);
      }
      setDraggingId(null);
      setDropTargetId(null);
    };

  return (
    <>
      {ordered.map((item) => {
        const dragHandle = (
          <button
            type="button"
            draggable
            onDragStart={handleHandleDragStart(item.id)}
            onDragEnd={handleHandleDragEnd}
            onKeyDown={handleHandleKeyDown(item.id)}
            aria-label={t('patient.sidePanels.dragHandleAriaLabel')}
            className="cursor-grab active:cursor-grabbing text-muted-foreground/60 hover:text-foreground p-1 -m-1"
          >
            <GripVertical className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
        );
        const isDragging = draggingId === item.id;
        const isDropTarget =
          dropTargetId === item.id &&
          draggingId !== null &&
          draggingId !== item.id;

        return (
          <div
            key={item.id}
            ref={(node) => {
              if (node !== null) cardRefs.current.set(item.id, node);
              else cardRefs.current.delete(item.id);
            }}
            onDragOver={handleCardDragOver(item.id)}
            onDragLeave={handleCardDragLeave(item.id)}
            onDrop={handleCardDrop(item.id)}
            className={cn(
              'rounded-lg transition-[opacity,box-shadow]',
              isDragging && 'opacity-50',
              isDropTarget && 'ring-2 ring-primary/50 ring-offset-2',
            )}
          >
            {item.render(dragHandle)}
          </div>
        );
      })}
    </>
  );
}
